import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/Card';
import Button from '../components/Button';
import {
  DASHBOARD_TYPE_GRID,
  typeGroupColor,
  getTypeGroup,
  GROUP_COLORS,
  GROUP_LABELS,
  type TypeGroup,
} from '../lib/constants';
import { api } from '../lib/api';
import type { Employee, Department, Team } from '../lib/types';

// Extended alert type from API (includes name/employee_id mapped from anonymized labels)
interface DashboardBurnoutAlert {
  member_label: string;
  employee_id: string | null;
  name: string;
  type: string;
  current_role: string;
  risk_level: 'high' | 'medium';
  risk_summary: string;
  warning_signs: string[];
  prevention_actions: string[];
  talent_note: string;
  department_name: string | null;
}
import { useAuth } from '../hooks/useAuth';

// ─── SVG Icons ───
const Icons = {
  people: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  team: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" /><path d="M12 2a10 10 0 0 1 0 20 10 10 0 0 1 0-20" />
      <path d="M2 12h4" /><path d="M18 12h4" /><path d="M12 2v4" /><path d="M12 18v4" />
    </svg>
  ),
  building: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
      <path d="M9 22v-4h6v4" /><path d="M8 6h.01" /><path d="M16 6h.01" />
      <path d="M8 10h.01" /><path d="M16 10h.01" /><path d="M8 14h.01" /><path d="M16 14h.01" />
    </svg>
  ),
  alert: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FF6B6B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  remote: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#4A7CFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 0 0 1 1h3m10-11l2 2m-2-2v10a1 1 0 0 1-1 1h-3m-4 0v-4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v4" />
    </svg>
  ),
  office: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
      <path d="M9 22v-4h6v4" /><path d="M8 6h.01" /><path d="M16 6h.01" />
      <path d="M8 10h.01" /><path d="M16 10h.01" /><path d="M8 14h.01" /><path d="M16 14h.01" />
    </svg>
  ),
  hybrid: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#FBBF24" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 3 21 3 21 8" /><line x1="4" y1="20" x2="21" y2="3" />
      <polyline points="21 16 21 21 16 21" /><line x1="15" y1="15" x2="21" y2="21" />
      <line x1="4" y1="4" x2="9" y2="9" />
    </svg>
  ),
};

// ─── Stat Card ───
function StatCard({ icon, label, value, unit, accent }: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  unit?: string;
  accent?: string;
}) {
  return (
    <Card className="flex-1 min-w-[140px]">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[13px] text-text-sub">{label}</span>
        <span className="text-text-muted">{icon}</span>
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-3xl font-bold" style={{ color: accent }}>{value}</span>
        {unit && <span className="text-sm text-text-sub">{unit}</span>}
      </div>
    </Card>
  );
}

// ─── 16 Type Grid Cell ───
function TypeCell({ type, count }: { type: string; count: number }) {
  const color = typeGroupColor(type);
  return (
    <div
      className="rounded-lg p-2 sm:p-3 transition-all hover:scale-[1.02]"
      style={{ background: `${color}15`, border: `1px solid ${color}40` }}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm sm:text-base font-bold tracking-wide" style={{ color }}>{type}</span>
        <span className="text-lg sm:text-2xl font-bold text-text-main">{count}</span>
      </div>
    </div>
  );
}

// ─── Department Stacked Bar ───
function DeptTypeBar({ dept, employees }: { dept: Department; employees: Employee[] }) {
  const members = employees.filter(e => e.department_id === dept.id && e.personality_type);
  const groupCounts: Record<TypeGroup, number> = { NT: 0, NF: 0, SJ: 0, SP: 0 };
  members.forEach(m => {
    const g = getTypeGroup(m.personality_type);
    if (g) groupCounts[g]++;
  });
  const total = members.length || 1;
  const groups: TypeGroup[] = ['NT', 'NF', 'SJ', 'SP'];

  return (
    <div className="flex items-center gap-2 mb-3">
      <div className="w-20 text-xs text-text-sub truncate text-right shrink-0">{dept.name}</div>
      <div className="flex-1 flex h-6 rounded overflow-hidden bg-surface">
        {groups.map(g => {
          const w = (groupCounts[g] / total) * 100;
          if (w === 0) return null;
          return (
            <div
              key={g}
              className="h-full flex items-center justify-center text-[10px] font-bold text-white"
              style={{ width: `${w}%`, background: GROUP_COLORS[g], minWidth: groupCounts[g] > 0 ? '24px' : 0 }}
            >
              {groupCounts[g]}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Work Style Circle (Mobile) ───
function WorkStyleCircle({ icon, label, count, color }: {
  icon: React.ReactNode;
  label: string;
  count: number;
  color: string;
}) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <span className="text-[12px] text-text-sub">{label}</span>
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center"
        style={{ border: `3px solid ${color}`, background: `${color}10` }}
      >
        {icon}
      </div>
      <span className="text-sm font-bold text-text-main">{count}名</span>
    </div>
  );
}

// ─── Main Dashboard ───
export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [burnoutAlerts, setBurnoutAlerts] = useState<DashboardBurnoutAlert[]>([]);
  const [ruleBasedAlerts, setRuleBasedAlerts] = useState<Array<{
    employee_id: string; name: string; personality_type: string; job_description: string;
    position: string; department_name: string; risk_level: 'high' | 'medium'; match_count: number;
  }>>([]);
  const [burnoutLoading, setBurnoutLoading] = useState(false);
  const [burnoutOrgNote, setBurnoutOrgNote] = useState('');

  const load = useCallback(async () => {
    try {
      const [empRes, deptRes, teamRes] = await Promise.all([
        api.employees.list(),
        api.departments.list(),
        api.teams.list(),
      ]);
      setEmployees(empRes.employees as unknown as Employee[]);
      setDepartments(deptRes.departments as unknown as Department[]);
      setTeams(teamRes.teams as unknown as Team[]);
    } catch { /* silently fail */ }
    finally { setLoading(false); }
  }, []);

  const [burnoutCheckedOnce, setBurnoutCheckedOnce] = useState(false);

  const loadBurnoutAlerts = useCallback(async (forceRefresh = false) => {
    setBurnoutLoading(true);
    try {
      const res = await api.ai.burnoutAlerts(forceRefresh);
      setBurnoutAlerts(res.alerts as unknown as DashboardBurnoutAlert[] || []);
      setRuleBasedAlerts(res.rule_based_alerts as unknown as typeof ruleBasedAlerts || []);
      setBurnoutOrgNote(res.organization_note as unknown as string || '');
      setBurnoutCheckedOnce(true);
    } catch { /* silently fail */ }
    finally { setBurnoutLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Don't auto-load burnout alerts to save API calls - user triggers it

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-text-muted text-sm">読み込み中...</div>;
  }

  // ─── Computed data ───
  const noTypeCount = employees.filter(e => !e.personality_type).length;
  const typeCounts: Record<string, number> = {};
  employees.forEach(e => {
    if (e.personality_type) typeCounts[e.personality_type] = (typeCounts[e.personality_type] || 0) + 1;
  });
  const styleCounts = {
    remote: employees.filter(e => e.work_style === 'remote').length,
    office: employees.filter(e => e.work_style === 'office').length,
    hybrid: employees.filter(e => e.work_style === 'hybrid').length,
  };
  const maxStyle = Math.max(styleCounts.remote, styleCounts.office, styleCounts.hybrid, 1);

  const workStyleItems = [
    { key: 'remote' as const, label: 'リモート', icon: Icons.remote, color: '#4A7CFF' },
    { key: 'office' as const, label: 'オフィス', icon: Icons.office, color: '#4ADE80' },
    { key: 'hybrid' as const, label: 'ハイブリッド', icon: Icons.hybrid, color: '#FBBF24' },
  ];

  // Greeting name
  const userName = user?.company_name || '';

  return (
    <div>
      {/* Mobile: Greeting */}
      <div className="lg:hidden mb-4">
        <h2 className="text-xl font-bold text-text-main">
          {userName ? `こんにちは、${userName}` : 'ダッシュボード'}
        </h2>
      </div>
      {/* Desktop: Title */}
      <h2 className="hidden lg:block text-2xl font-bold text-text-main mb-6">ダッシュボード</h2>

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <StatCard icon={Icons.people} label="社員数" value={employees.length} unit="名" />
        <StatCard icon={Icons.team} label="チーム数" value={teams.length} />
        <StatCard icon={Icons.building} label="部署数" value={departments.length} />
        <StatCard icon={Icons.alert} label="タイプ未入力" value={noTypeCount} accent={noTypeCount > 0 ? '#FF6B6B' : undefined} />
      </div>

      {/* Main Content: Desktop 2-column, Mobile stacked */}
      <div className="flex gap-4 flex-wrap lg:flex-nowrap">

        {/* Left: 16 Type Grid */}
        <div className="flex-[2] min-w-[300px]">
          <Card>
            <div className="text-[15px] font-semibold text-text-main mb-4">16タイプ性格分析</div>
            <div className="grid grid-cols-4 gap-2">
              {DASHBOARD_TYPE_GRID.flat().map(t => (
                <TypeCell key={t} type={t} count={typeCounts[t] || 0} />
              ))}
            </div>
            {/* Group Legend */}
            <div className="flex gap-4 mt-4 pt-3 border-t border-border flex-wrap">
              {(['NT', 'NF', 'SJ', 'SP'] as TypeGroup[]).map(g => (
                <div key={g} className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-sm" style={{ background: GROUP_COLORS[g] }} />
                  <span className="text-xs text-text-sub">{GROUP_LABELS[g]}（{g}）</span>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Right Column */}
        <div className="flex-1 min-w-[260px] flex flex-col gap-4">
          {/* Department Type Composition */}
          <Card>
            <div className="text-[15px] font-semibold text-text-main mb-3">部署別タイプ構成</div>
            {departments.length > 0 ? (
              <>
                <div className="flex gap-3 mb-3 flex-wrap">
                  {(['NT', 'NF', 'SJ', 'SP'] as TypeGroup[]).map(g => (
                    <div key={g} className="flex items-center gap-1">
                      <div className="w-2.5 h-2.5 rounded-sm" style={{ background: GROUP_COLORS[g] }} />
                      <span className="text-[10px] text-text-muted">{g}</span>
                    </div>
                  ))}
                </div>
                {departments.map(dept => (
                  <DeptTypeBar key={dept.id} dept={dept} employees={employees} />
                ))}
              </>
            ) : (
              <div className="text-sm text-text-muted py-4">部署が登録されていません</div>
            )}
          </Card>

          {/* Work Style Summary */}
          <Card>
            <div className="text-[15px] font-semibold text-text-main mb-4">働き方サマリー</div>

            {/* Mobile: Circle icons */}
            <div className="flex justify-around lg:hidden mb-2">
              {workStyleItems.map(item => (
                <WorkStyleCircle
                  key={item.key}
                  icon={item.icon}
                  label={item.label}
                  count={styleCounts[item.key]}
                  color={item.color}
                />
              ))}
            </div>

            {/* Desktop: List with progress bars */}
            <div className="hidden lg:block">
              {workStyleItems.map((item) => (
                <div key={item.key} className="flex items-center gap-3 py-2.5">
                  <span className="shrink-0">{item.icon}</span>
                  <span className="text-[13px] text-text-sub flex-1">{item.label}</span>
                  <div className="w-24 h-1.5 rounded bg-surface overflow-hidden">
                    <div
                      className="h-full rounded transition-all duration-500"
                      style={{ width: `${(styleCounts[item.key] / maxStyle) * 100}%`, background: item.color }}
                    />
                  </div>
                  <span className="text-lg font-bold text-text-main w-8 text-right">{styleCounts[item.key]}</span>
                </div>
              ))}
            </div>

            <Button primary small onClick={() => navigate('/employees')} className="w-full mt-3">
              詳細を見る →
            </Button>
          </Card>
        </div>
      </div>

      {/* Burnout Risk Alert Section */}
      <div className="mt-5">
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FF6B6B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <span className="text-[15px] font-semibold text-text-main">燃え尽きリスクアラート</span>
            </div>
            <Button
              small
              primary={!burnoutLoading && !burnoutCheckedOnce}
              onClick={() => loadBurnoutAlerts(burnoutCheckedOnce)}
              disabled={burnoutLoading}
            >
              {burnoutLoading ? 'AI分析中...' : burnoutCheckedOnce ? '再チェック（キャッシュクリア）' : 'チェックする'}
            </Button>
          </div>

          {burnoutLoading && (
            <div className="text-center py-6 text-text-muted text-sm">
              <div className="animate-pulse">全社員のタイプと業務の適合度をAIで分析中...</div>
            </div>
          )}

          {!burnoutLoading && burnoutAlerts.length === 0 && ruleBasedAlerts.length === 0 && (
            <div className="text-sm text-text-muted py-3">
              「チェックする」ボタンで、社員のタイプと業務の不一致を自動検出します。
            </div>
          )}

          {!burnoutLoading && (burnoutAlerts.length > 0 || ruleBasedAlerts.length > 0) && (
            <div className="space-y-3">
              {/* AI detailed alerts */}
              {burnoutAlerts.map((alert, i) => (
                <div
                  key={i}
                  className="rounded-lg p-3 cursor-pointer hover:brightness-110 transition-all"
                  style={{
                    background: alert.risk_level === 'high' ? 'rgba(255,107,107,0.1)' : 'rgba(251,191,36,0.1)',
                    border: `1px solid ${alert.risk_level === 'high' ? 'rgba(255,107,107,0.3)' : 'rgba(251,191,36,0.3)'}`,
                  }}
                  onClick={() => alert.employee_id && navigate(`/employees/${alert.employee_id}`)}
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${
                      alert.risk_level === 'high' ? 'bg-accent/20 text-accent' : 'bg-warning/20 text-warning'
                    }`}>
                      {alert.risk_level === 'high' ? 'HIGH' : 'MEDIUM'}
                    </span>
                    <span className="text-sm font-semibold text-text-main">{alert.name || alert.member_label}</span>
                    <span className="text-xs text-text-muted">({alert.type})</span>
                    <span className="text-xs text-text-sub ml-auto">{alert.current_role}</span>
                  </div>
                  <p className="text-xs text-text-sub leading-relaxed">{alert.risk_summary}</p>
                  {alert.talent_note && (
                    <p className="text-xs text-primary mt-1">{alert.talent_note}</p>
                  )}
                </div>
              ))}

              {/* Rule-based only alerts (when AI didn't return details) */}
              {ruleBasedAlerts.filter(r => !burnoutAlerts.some(a => a.employee_id === r.employee_id)).map((alert, i) => (
                <div
                  key={`rule-${i}`}
                  className="rounded-lg p-3 cursor-pointer hover:brightness-110 transition-all"
                  style={{
                    background: alert.risk_level === 'high' ? 'rgba(255,107,107,0.08)' : 'rgba(251,191,36,0.08)',
                    border: `1px solid ${alert.risk_level === 'high' ? 'rgba(255,107,107,0.2)' : 'rgba(251,191,36,0.2)'}`,
                  }}
                  onClick={() => navigate(`/employees/${alert.employee_id}`)}
                >
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${
                      alert.risk_level === 'high' ? 'bg-accent/20 text-accent' : 'bg-warning/20 text-warning'
                    }`}>
                      {alert.risk_level === 'high' ? 'HIGH' : 'MEDIUM'}
                    </span>
                    <span className="text-sm font-semibold text-text-main">{alert.name}</span>
                    <span className="text-xs text-text-muted">({alert.personality_type})</span>
                    <span className="text-xs text-text-sub ml-auto truncate max-w-[150px]">{alert.job_description}</span>
                  </div>
                </div>
              ))}

              {/* Organization note */}
              {burnoutOrgNote && (
                <div className="pt-2 border-t border-border">
                  <p className="text-xs text-text-sub leading-relaxed">{burnoutOrgNote}</p>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
